import React from "react";
import { Avatar, Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { IosShareOutlined as IosShareOutlinedIcon } from "./icons/FigmaControlIcons";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const cardSx = {
  p: { xs: 2.5, sm: 3 }, borderRadius: "24px",
  border: "1px solid rgba(15, 23, 42, 0.08)", backgroundColor: "#FFFFFF", minWidth: 0,
};
const headingSx = {
  fontFamily: "Be Vietnam Pro", fontSize: 14, fontWeight: 800,
  textTransform: "uppercase", letterSpacing: "0.06em", color: BLUE, mb: 1.5,
};
const textSx = {
  fontFamily: "Be Vietnam Pro", fontSize: 15, lineHeight: 1.8,
  color: "#344054", whiteSpace: "pre-wrap", overflowWrap: "anywhere",
};
const isPresent = value => Boolean(value && !["—", "-"].includes(String(value).trim()));

function FieldValue({ field }) {
  const value = String(field.value || "—");
  let href = "";
  if (["Company Website", "Company Career Website", "LinkedIn"].includes(field.label)) {
    if (/^https?:\/\//i.test(value)) href = value;
    else if (/^(?:www\.)?[\w-]+(?:\.[\w-]+)+(?:\/[^\s]*)?$/.test(value)) href = `https://${value}`;
  }
  if (field.label === "Email" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) href = `mailto:${value}`;
  if (field.label === "Phone" && /^[+\d\s().-]+$/.test(value)) href = `tel:${value.replace(/[^+\d]/g, "")}`;
  return <Typography component={href ? "a" : "p"} href={href || undefined}
    target={/^https?:/.test(href) ? "_blank" : undefined} rel={href ? "noreferrer" : undefined}
    sx={{ ...textSx, fontSize: 14, color: href ? BLUE : "#344054", textDecoration: "none", "&:hover": href ? { textDecoration: "underline" } : {} }}>
    {value}
  </Typography>;
}

function Fields({ fields }) {
  return <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
    {fields.map(field => <Box key={field.label} sx={{ minWidth: 0 }}>
      <Typography component="dt" sx={{ fontFamily: "Be Vietnam Pro", fontSize: 13, fontWeight: 700, color: "#667085", mb: 0.5 }}>{field.label}</Typography>
      <Box component="dd" sx={{ m: 0 }}><FieldValue field={field} /></Box>
    </Box>)}
  </Box>;
}

export default function PublicLeadProfile({ share }) {
  const fields = Array.isArray(share.fields) ? share.fields : [];
  const value = label => fields.find(field => field.label === label)?.value || "—";
  const narrativeLabels = ["Description", "Key Responsibilities", "Qualifications"];
  const skillLabels = ["Hard skills", "Soft skills"];
  const contactLabels = ["Contact", "Title", "Email", "Phone", "LinkedIn"];
  const summaryFields = fields.filter(field => ![...narrativeLabels, ...skillLabels, ...contactLabels].includes(field.label));
  const contactFields = fields.filter(field => contactLabels.includes(field.label));
  const company = share.subtitle || value("Company");
  const chips = [share.badge, value("Location"), value("Job Type"), value("Workplace")].filter(isPresent);

  return <Box component="main" sx={{
    minHeight: "100vh",
    background: "radial-gradient(circle at top left, rgba(156,79,255,0.15), transparent 28%), radial-gradient(circle at bottom right, rgba(52,81,244,0.12), transparent 24%), linear-gradient(180deg, #F8FAFF 0%, #EEF2FF 100%)",
    px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 },
  }}>
    <Box sx={{ maxWidth: 1240, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: { xs: 3, md: 4 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 42, height: 42, borderRadius: "14px", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)", boxShadow: "0 14px 28px rgba(52, 81, 244, 0.2)" }}>
            <IosShareOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ ...headingSx, fontSize: 13, letterSpacing: "0.08em", mb: 0 }}>Shared Through RPO AI</Typography>
            <Typography sx={{ fontFamily: "Be Vietnam Pro", fontSize: { xs: 18, sm: 22 }, fontWeight: 800, color: "#101828" }}>Lead Shared Link</Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          {[
            { label: "No login required", color: BLUE, border: "rgba(52,81,244,0.12)", Icon: PublicOutlinedIcon },
            { label: "Read-only access", color: PURPLE, border: "rgba(156,79,255,0.14)", Icon: LockOutlinedIcon },
          ].map(({ label, color, border, Icon }) => <Chip key={label} icon={<Icon sx={{ color: `${color} !important` }} />} label={label}
            sx={{ height: 36, borderRadius: "999px", backgroundColor: "#FFFFFF", border: `1px solid ${border}`, color, fontFamily: "Be Vietnam Pro", fontWeight: 700, px: 1 }} />)}
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.4fr) minmax(360px, 0.9fr)" }, gap: { xs: 2.5, lg: 3 }, alignItems: "start" }}>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ ...cardSx, p: 0, borderRadius: "28px", overflow: "hidden" }}>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 }, background: "linear-gradient(135deg, rgba(52,81,244,0.08) 0%, rgba(156,79,255,0.12) 100%)", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
                <Avatar sx={{ width: 84, height: 84, border: "4px solid rgba(255,255,255,0.75)", boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)", background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)", fontFamily: "Be Vietnam Pro", fontSize: 30, fontWeight: 800 }}>
                  {(isPresent(company) ? company : share.title || "L").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h1" sx={{ fontFamily: "Be Vietnam Pro", fontSize: { xs: 26, sm: 32 }, lineHeight: 1.08, fontWeight: 900, color: "#101828", mb: 1, overflowWrap: "anywhere" }}>{share.title}</Typography>
                  <Typography sx={{ ...textSx, fontSize: { xs: 15, sm: 18 }, lineHeight: 1.6, mb: 1.5 }}>{company}</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {[...new Set(chips)].map(chip => <Chip key={chip} label={chip} sx={{ minHeight: 32, height: "auto", maxWidth: "100%", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 }, borderRadius: "999px", backgroundColor: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", fontFamily: "Be Vietnam Pro", fontWeight: 700, color: "#344054" }} />)}
                  </Stack>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Typography component="h2" sx={headingSx}>Description</Typography>
              <Typography sx={textSx}>{value("Description")}</Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={cardSx}>
            <Typography component="h2" sx={headingSx}>Core Skills</Typography>
            <Stack spacing={2}>
              {skillLabels.map(label => <Box key={label}>
                <Typography component="h3" sx={{ fontFamily: "Be Vietnam Pro", fontSize: 13, fontWeight: 700, color: "#667085", mb: 1 }}>{label}</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {isPresent(value(label)) ? [...new Set(String(value(label)).split(/[,\n]+/).map(skill => skill.trim()).filter(Boolean))].map(skill => <Chip key={skill} label={skill} sx={{ minHeight: 34, height: "auto", maxWidth: "100%", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 }, borderRadius: "999px", backgroundColor: "#EEF4FF", color: BLUE, fontFamily: "Be Vietnam Pro", fontWeight: 700 }} />) : <Typography sx={textSx}>—</Typography>}
                </Stack>
              </Box>)}
            </Stack>
          </Paper>

          {narrativeLabels.slice(1).map(label => <Paper key={label} elevation={0} sx={cardSx}>
            <Typography component="h2" sx={headingSx}>{label}</Typography>
            <Typography sx={textSx}>{value(label)}</Typography>
          </Paper>)}
        </Stack>

        <Paper component="aside" aria-label="Lead details" elevation={0} sx={{ ...cardSx, borderRadius: "28px", boxShadow: "0 20px 40px rgba(15, 23, 42, 0.06)" }}>
          <Typography component="h2" sx={{ ...headingSx, color: PURPLE, fontSize: 13, letterSpacing: "0.08em", mb: 0.75 }}>Lead Summary</Typography>
          <Typography sx={{ fontFamily: "Be Vietnam Pro", fontSize: 26, lineHeight: 1.15, fontWeight: 900, color: "#101828", mb: 2.25 }}>Opportunity details</Typography>
          <Fields fields={summaryFields} />
          <Divider sx={{ my: 2.5 }} />
          <Typography component="h2" sx={headingSx}>Contact Details</Typography>
          <Fields fields={contactFields} />
        </Paper>
      </Box>
    </Box>
  </Box>;
}
